import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiLock } from '@/components/react-icons/main';
import sendToast from '@/lib/sendToast';
import openOauthWindow from '@/lib/openOauthWindow';
import { getCredentialById, getM2MOauthToken } from '@/redux/actions/gravityActions.js';
import credentialActions from '@/redux/actions/credentialActions';
import appActions from '@/screens/apps/redux/actions/appActions';
import appScreenSelectors from '../../../redux/selectors/appScreenSelectors';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import Button from '@/components/Button.jsx';
import selectors from '@/redux/selectors/selectors';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { CREDENTIAL_LABEL_SOURCE_MANUAL } from '@shared/schema/credentialLabelSource.js';

const AuthenticateButton = ({ size, className, hideLockIcon }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const app = useSelector(appScreenSelectors.activeApp);
  const appSlug = app?.slug;
  const appId = app?.id;
  const authentication = app?.authentication;
  const authTypeCredentials = app?.authTypeCredentials || {};
  const selectedCredential = useSelector(appScreenSelectors.activeAppDefaultTestCred);
  const oauth2 = authentication?.find((n) => n.type === 'oAuth2');
  const credentialAuthId = selectedCredential?.meta?.authId || selectedCredential?.secrets?.authId;
  const credentialAuthType = selectedCredential?.meta?.type || selectedCredential?.secrets?.type;
  const isSelectedCredentialOAuth2 = credentialAuthId === oauth2?.id || credentialAuthType === 'oAuth2';
  const credential = isSelectedCredentialOAuth2 ? selectedCredential : null;
  const credentialId = credential?.credentialId;
  const credentialCompanyId = credential?.companyId || selectedCredential?.companyId;
  const seedCredentialSecrets = useMemo(() => {
    return (oauth2?.arguments || []).reduce((acc, argument) => {
      const value = selectedCredential?.secrets?.[argument?.key] ?? selectedCredential?.[argument?.key];

      if (value === undefined || value === null || String(value).trim() === '') {
        return acc;
      }

      return { ...acc, [argument.key]: value };
    }, {});
  }, [oauth2?.arguments, selectedCredential]);
  const isLoadingM2MOauthToken = useArgSelector(selectors.isLoading, `getM2MOauthToken-${credentialId}`);
  const isValid = credential?.status === 'valid';

  const isUsedInProduction = Number(credential?.activeWorkflowCount) > 0;
  const hasRequiredArgs = oauth2?.arguments?.some((n) => n.required);
  const hasMissingRequiredArgs = (oauth2?.arguments || []).some((argument) => {
    if (!argument?.required) {
      return false;
    }

    return String(seedCredentialSecrets?.[argument.key] || '').trim() === '';
  });
  const isM2M = oauth2?.options?.enableM2M;
  const isM2MReconnecting = isLoadingM2MOauthToken && isM2M;

  const handleAuthenticateClick = async (event, published) => {
    const ctrlKey = event.ctrlKey || event.metaKey;

    let effectiveCredentialId = credentialId;

    if (hasRequiredArgs && hasMissingRequiredArgs) {
      return sendToast({ message: 'Please fill the required test connection arguments.', duration: 3000 });
    }

    if (!effectiveCredentialId) {
      effectiveCredentialId = await credentialActions.set({
        companyId: credentialCompanyId,
        appId,
        label: 'Apps Screen Test Connection',
        meta: { type: 'oAuth2', authId: oauth2?.id, label: 'Apps Screen Test Connection', labelSource: CREDENTIAL_LABEL_SOURCE_MANUAL },
        secrets: { type: 'oAuth2', authId: oauth2?.id, label: 'Apps Screen Test Connection', ...seedCredentialSecrets }
      });
      await waitForSyncReady();
    }

    if (oauth2?.id && effectiveCredentialId) {
      appActions.set('activeAppsScreenAuthId', oauth2.id);
      appActions.set('authTypeCredentials', { ...authTypeCredentials, [oauth2.id]: effectiveCredentialId });
    }

    if (!isM2M) {
      setIsAuthenticating(true);
      window.gravityHandleOAuth2Callback = async (response) => {
        if (response?.success && response?.data?.credentialId) {
          // appActions.set(`authentication[id:${authentication?.id}].credentialId`, response?.data?.credentialId);
          sendToast({ message: 'Successfully authenticated' });
          await getCredentialById(response?.data?.credentialId, { secrets: true, companyId: credentialCompanyId });
          return;
        }
        console.error('Error getting oauth callback', response);
        // appActions.set(`authentication[id:${authentication?.id}].credentialId`, null);
      };
      openOauthWindow({
        admin: true,
        credentialId: effectiveCredentialId,
        appSlug,
        debugMode: ctrlKey,
        draft: !published,
        onClose: () => getCredentialById(effectiveCredentialId, { secrets: true, companyId: credentialCompanyId })
      });
      setIsAuthenticating(false);
    }

    if (isM2M) {
      await getM2MOauthToken(
        {
          appSlug,
          credentialId: effectiveCredentialId,
          companyId: credentialCompanyId,
          draft: !published
        },
        (data) => {
          if (data?.success) {
            sendToast({ message: 'Successfully authenticated', type: 'success', duration: 4000 });
          } else {
            sendToast({ message: 'Something went wrong authenticating', type: 'error', duration: 4000 });
          }
        }
      );
    }
  };

  const handleDeleteClick = () => {
    if (isUsedInProduction) {
      setModalState('cannotDeleteCredential', { open: true, credentialId: credential?.credentialId });
      return;
    }
    credentialActions.deleteCredential({ credentialId: credential?.credentialId, companyId: 'MINDCLOUD' });
  };

  const handleOptionClick = (option, e) => {
    if (option.value === 'authenticate_published') {
      handleAuthenticateClick(e, true);
    } else if (option.value === 'delete') {
      handleDeleteClick();
    }
  };

  const options = [{ label: 'Authenticate Published', value: 'authenticate_published' }, ...(isValid ? [{ label: 'Delete', value: 'delete' }] : [])];

  return (
    <Button className={`authenticate-button ${isValid ? 'authenticated' : ''} ${className}`} size={size} icon={hideLockIcon ? null : FiLock} options={options} onClick={handleAuthenticateClick} onOptionClick={handleOptionClick} loading={isAuthenticating || isM2MReconnecting} disabled={isM2MReconnecting || isAuthenticating}>
      {isValid ? 'Authenticated' : isAuthenticating || isM2MReconnecting ? 'Authenticating...' : 'Authenticate'}
    </Button>
  );
};

export default AuthenticateButton;
